// Updated: 2026-08-14
import type {
  CheckAbusePatternsResponse,
  FollowArtistSuccessResponse,
  UnfollowArtistSuccessResponse,
  GetUserRelationsResponse,
} from '@mrb91002/shared-types'

import { secureFetch, SECURE_API_BASE } from './helpers'

/**
 * Follow an artist (by screen name, logged-in user)
 */
export async function followUser(artistId: string, idToken: string): Promise<FollowArtistSuccessResponse> {
  return secureFetch<FollowArtistSuccessResponse>(`${SECURE_API_BASE}/follow/${encodeURIComponent(artistId)}`, idToken, {
    method: 'POST',
  })
}

/**
 * Unfollow an artist (by screen name, logged-in user)
 */
export async function unfollowUser(artistId: string, idToken: string): Promise<UnfollowArtistSuccessResponse> {
  return secureFetch<UnfollowArtistSuccessResponse>(`${SECURE_API_BASE}/follow/${encodeURIComponent(artistId)}`, idToken, {
    method: 'DELETE',
  })
}

/**
 * Fetch all user relations (favorites, following, followers) for the logged-in user.
 * GET /user-relations
 *
 * `opts.signal` is optional request cancellation forwarded from a React Query `queryFn`, spread
 * conditionally so a caller that omits it makes a byte-identical request — the two READ fetchers in
 * `./notifications.ts` take the same parameter in the same trailing position for the same reason.
 * `secureFetch` (`./helpers.ts:77-92`) destructures `signal` out of its `RequestInit` and threads it
 * into BOTH the initial request and the 401 refresh-retry, so an aborted relations read cancels at both
 * hops and nothing about the token-refresh path changes (react-query-user-relations, Req 2.7, 11.2).
 *
 * NO `signal` on `followUser` / `unfollowUser` above, deliberately: React Query does not abort
 * mutations, so a `signal` on a write transport would be untested surface (Req 11.3).
 */
export async function fetchUserRelations(
  idToken: string,
  opts?: { signal?: AbortSignal },
): Promise<GetUserRelationsResponse> {
  return secureFetch<GetUserRelationsResponse>(`${SECURE_API_BASE}/user-relations`, idToken, {
    method: 'GET',
    ...(opts?.signal ? { signal: opts.signal } : {}),
  })
}

/**
 * Check for abuse patterns
 */
export async function checkAbusePatterns(idToken: string): Promise<CheckAbusePatternsResponse> {
  return secureFetch<CheckAbusePatternsResponse>(`${SECURE_API_BASE}/abuse-check`, idToken, {
    method: 'GET',
  })
}
